import { Dispatch, Fragment, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { NavLink, useLocation } from 'react-router';
import { Drawer, Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Action } from 'redux';

import { HttpStatusCodeEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { IndividualExtendedInfoData } from '../../lib/models/enroll';
import { RootState } from '../../lib/store';
import {
  getAuthUserTradingAcount as getAuthUserTradingAcountCall,
  IndividualExtendedInfoPayload,
} from '../../lib/store/common-actions';

import PortalLogo from '../../assets/img/logo_acetrade_large.svg';
import PortalLogoSmall from '../../assets/img/logo_acetrade_small.svg';
import config from '../../config';
import { useBrowserWidth, useFlags, useIsMobile } from '../../hooks';

import PopoverComponent from './Popover/PopoverComponent';
import { MENU_FEATURE_FLAGS, MENU_LIST, SUPPORT_LIST } from './Sidebar.constans';
import { SideBarItem } from './Sidebar.types';

import styles from './Sidebar.module.scss';

const LARGE_WIDTH = 1439;

const renderImage = (item: SideBarItem, isActive: boolean) =>
  (isActive ? item.activeImg : item.img);

const Sidebar = () => {
  const { browserWidthSize } = useBrowserWidth();

  const location = useLocation();
  const { isMobile } = useIsMobile();

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [popoverLabel, setPopoverLabel] = useState<string>('');
  const [popoverStatus, setPopoverStatus] = useState<HTMLDivElement | null>(null);
  const isSmallLogo = browserWidthSize <= LARGE_WIDTH;
  const menuFlags = useFlags(MENU_FEATURE_FLAGS);
  const menuList = useMemo(
    () => MENU_LIST.filter(item => (
      (!item.featureFlag || menuFlags[item.featureFlag].enabled)
      && (!item.hiddenByFeatureFlag || !menuFlags[item.hiddenByFeatureFlag].enabled)
    )),
    [menuFlags],
  );

  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [isMobile]);

  const handlePopover = (event, label: string) => {
    setPopoverLabel(label);
    setPopoverStatus(event.currentTarget);
  };

  const isActiveBtn = (item: SideBarItem): boolean => location.pathname === item.path;

  const handleCLickBtn = () => {
    if (isMobile) {
      return setIsOpen(false);
    }

    return null;
  };

  const useStyles = makeStyles((theme) => ({
    drawerPaper: {
      background: 'transparent',
      border: 'none',
      overflow: 'hidden',
    },
  }));

  const classes = useStyles();

  const isMediumBrowserSize = !isMobile && browserWidthSize <= LARGE_WIDTH;

  return (
    <>
      {isOpen && (
        <Drawer
          classes={{
            paper: classes.drawerPaper,
          }}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: 'transparent',
            },
          }}
          transitionDuration={40}
          variant={!isMobile ? 'persistent' : 'temporary'}
          open={isOpen}
          onClose={() => setIsOpen(false)}
        >
          <div className={styles.wrapperSidebar}>
            <div className={styles.logoWrapper}>
              <img
                src={isSmallLogo ? PortalLogoSmall : PortalLogo}
                className={styles.portalLogo}
                alt="logo"
              />
            </div>
            <div className={styles.wrapperButton}>
              {menuList.map((item: SideBarItem) => (
                <NavLink
                  id={item.id}
                  key={item.id}
                  to={item.path}
                  state={{
                    isClickedBtnSidebar: true,
                  }}
                  aria-owns={popoverStatus ? 'mouse-over-popover' : undefined}
                  aria-haspopup="true"
                  onMouseEnter={(event) => handlePopover(event, item.label)}
                  onMouseLeave={() => setPopoverStatus(null)}
                  onClick={handleCLickBtn}
                >
                  {isMediumBrowserSize ? (
                    <div className={isActiveBtn(item) ? styles.activeImage : styles.image}>
                      {renderImage(item, isActiveBtn(item))}
                    </div>
                  ) : (
                    <div className={styles.button}>
                      <div className={isActiveBtn(item) ? styles.activeBtn : styles.btn}>
                        <div className={styles.imageAndLabel}>
                          {renderImage(item, isActiveBtn(item))}
                        </div>
                        <span>{item.label}</span>
                      </div>
                    </div>
                  )}
                </NavLink>
              ))}
            </div>
            <div className={isMediumBrowserSize ? styles.shortDivider : styles.longDivider} />
            <div className={[styles.wrapperButton, styles.helperAndPortalButtons].join(' ')}>
              {SUPPORT_LIST.map((item: SideBarItem) => (
                <Fragment key={item.id}>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    key={item.label}
                    href={`mailto:${config.links.aceHelpCenterUrl}`}
                    target="_blank"
                    className={
                      location.pathname === item.path ? styles.activeButton : styles.button
                    }
                    aria-owns={popoverStatus ? 'mouse-over-popover' : undefined}
                    aria-haspopup="true"
                    onMouseEnter={(event) => handlePopover(event, item.label)}
                    onMouseLeave={() => setPopoverStatus(null)}
                  >
                    {isMediumBrowserSize ? (
                      <div className={styles.image}>{renderImage(item, isActiveBtn(item))}</div>
                    ) : (
                      <div className={styles.button}>
                        <div className={styles.btn}>
                          <div className={styles.imageAndLabel}>
                            {renderImage(item, isActiveBtn(item))}
                          </div>
                          {lang.commonContactUs()}
                        </div>
                      </div>
                    )}
                  </Link>
                </Fragment>
              ))}
              {!isMobile && browserWidthSize < LARGE_WIDTH && (
                <PopoverComponent
                  open={Boolean(popoverStatus)}
                  popoverStatus={popoverStatus}
                  handlePopoverClose={() => setPopoverStatus(null)}
                  label={popoverLabel}
                />
              )}
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getAuthUserTradingAcounts: (data: IndividualExtendedInfoData) =>
    dispatch(
      getAuthUserTradingAcountCall({
        ...data,
        callName: 'getAccounts',
        status: HttpStatusCodeEnum.Ok,
      } as IndividualExtendedInfoPayload),
    ),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Sidebar);
